import { Injectable, inject, signal } from '@angular/core';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { HdGateInfoOpenBehavior } from '../gate-info/hd-gate-info-requested-context.model';
import { loadHdChartGlobalSettings } from '../chart-tabs/hd-chart-global-settings';
import { HdContentLanguageService } from '../hd-content-language.service';
import { HdPlanetInfoService } from '../hd-planet-info.service';
import { HdChartPointKey } from '../planet-grid/hd-planet-display-config';
import { HdPlanetInfoPanelComponent, HdPlanetInfoPanelData } from './hd-planet-info-panel.component';

/** Eén planeet-info-paneel: een andere planeet openen vervangt de inhoud. */
export const HD_PLANET_INFO_PANEL_KEY = 'planet-info';

/**
 * Opent het planeet-info-paneel (cheat-sheet-kaart + alle planetaire
 * invloeden). Volgt hetzelfde patroon als `HdFearsPanelService`: de
 * dock-/dialoogkeuze komt uit de globale `gateInfoOpenBehavior`-setting die de
 * chart-pagina's live doorgeven via {@link setOpenBehavior}.
 */
@Injectable({ providedIn: 'root' })
export class HdPlanetInfoPanelService {
  private readonly floatingPanels = inject(FloatingPanelService);
  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly openBehavior = signal<HdGateInfoOpenBehavior>(
    loadHdChartGlobalSettings().gateInfoOpenBehavior,
  );

  /** Live-sync van de `gateInfoOpenBehavior`-setting door de chart-pagina's. */
  setOpenBehavior(behavior: HdGateInfoOpenBehavior): void {
    this.openBehavior.set(behavior);
  }

  openForPlanet(planetKey: HdChartPointKey, origin?: { x: number; y: number }): void {
    const behavior = this.openBehavior();
    const docked = behavior === 'full-docked-keep' || behavior === 'full-docked-autoclose';
    const data: HdPlanetInfoPanelData = { planetKey };
    const label = this.dockTabLabel(planetKey);

    const ref = this.floatingPanels.openOrFocus<HdPlanetInfoPanelComponent, HdPlanetInfoPanelData>({
      key: HD_PLANET_INFO_PANEL_KEY,
      component: HdPlanetInfoPanelComponent,
      data,
      dockTab: { label, icon: 'planet-ringed' },
      dockZone: 'full',
      openInitiallyDocked: docked,
      redockOnReopen: docked,
      startPosition: origin != null
        ? { preferred: origin, hint: 'prefer-right-of-anchor' }
        : 'center',
    });
    // Een al open paneel toont voortaan de nieuw gekozen planeet.
    ref.replaceData({ ...data });
    ref.setDockTabLabel(label);
  }

  /** Planeetnaam in de content-taal (NL-fallback) als dock-tab-label. */
  private dockTabLabel(planetKey: HdChartPointKey): string {
    const lang = this.contentLang.effectiveLang();
    return this.planetInfo.planetInfoIn(planetKey, lang)?.name
      || this.planetInfo.planetInfoIn(planetKey, 'nl')?.name
      || planetKey;
  }
}
